/**
 * The station UI root (plan §6 M2-18): reads the store snapshot, builds the view (`models/view.ts`), runs the
 * focus model over its groups for keyboard and gamepad input (delivered synchronously through the `NavBus`), and
 * renders the title, station, death and ownership screens plus the confirm dialog.
 *
 * CL-6 (core-loop plan §4): the station's tab bar holds the primary tabs plus a More button over the grouped drawer
 * (`G.more`). Q/E (LB/RB) cycle the bar and the More button; Enter/A on More opens the drawer; Backspace/B and Esc
 * close it. The drawer's open state and the session's "new" dots are UI-local (no save change, Q9).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore, type ChangeEvent, type ReactNode } from 'react'
import type { CommandError } from '../career/result'
import { logger } from '../core/logger'
import type { M2Data } from '../data/m2Schemas'
import { UI_CONFIG, type UiConfig } from './config'
import type { CmdOutcome, ManualSlotId, SaveSlotId, UiStoreApi } from './contracts'
import type { ExternalTab, ExternalTabSource } from './external'
import { FocusButton, FocusContext, type FocusApi, type FocusHandlers } from './focus'
import { G, type TitleView } from './models/layout'
import { LOADING_LABEL, launchModel } from './models/station'
import { barIndexOf, moreSlot, NO_DOTS, trackPromotions, type TabDots } from './models/stationTabs'
import { activeTabId, buildView, type ExternalView, type StationView } from './models/view'
import { focusAt, focusKey, initialFocus, navigate, normalizeFocus, type FocusState, type NavBus, type NavInput } from './nav'
import type { UiController } from './screens/common'
import { DeathScreen, OwnershipScreen } from './screens/Moments'
import { StationScreen } from './screens/Station'
import { TitleScreen } from './screens/Title'
import './screens.css'

export interface AppProps {
  store: UiStoreApi
  data: M2Data
  nav: NavBus
  /** `canvas.requestPointerLock()`, called synchronously inside the Launch gesture. */
  requestPointerLock: () => void
  cfg?: UiConfig
  /** Extra station tabs another module mounts after M2's (M3 R22f: the Reach's Charts, Parts and Home). */
  external?: ExternalTabSource
  /** CL-6: the loop's Esc stack (`ctx.ui.escape`): Esc closes the More drawer before it reaches the pause menu. */
  escape?: (() => boolean)[]
}

const NO_TABS: readonly ExternalTab[] = Object.freeze([])

/** CL-6: the showing tab's slot on the bar (its index, or the More button's when it waits in the drawer). */
function activeTabIndex(v: StationView): number {
  return barIndexOf(v.tabGroups, activeTabId(v))
}

function errorOf(err: unknown): CommandError {
  return { code: 'storageError', message: err instanceof Error ? err.message : String(err) }
}

/** Saves `text` as a file download. */
function downloadText(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }))
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.rel = 'noopener'
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function App(props: AppProps): ReactNode {
  const { store, data, nav } = props
  const cfg = props.cfg ?? UI_CONFIG
  const state = useSyncExternalStore(store.subscribe, store.getState)
  const [titleView, setTitleView] = useState<TitleView>('main')
  const [localError, setLocalError] = useState<CommandError | null>(null)
  const [, rerender] = useReducer((n: number) => n + 1, 0)
  const rootRef = useRef<HTMLDivElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const importSlotRef = useRef<ManualSlotId | undefined>(undefined)
  const registry = useRef(new Map<string, FocusHandlers>())
  const focusRef = useRef<FocusState>({ group: '', index: 0, memory: {} })
  const contextRef = useRef('')
  const scrollRef = useRef(false)
  const lockRef = useRef(props.requestPointerLock)
  lockRef.current = props.requestPointerLock
  // External tabs (M3 R22f): which one shows (UI-local, like the title sub-view) and how many buttons it mounted.
  const [extActive, setExtActive] = useState<string | null>(null)
  const [extButtons, setExtButtons] = useState(0)
  // CL-6: the More drawer (UI-local) and, once it closes without a pick, the focus back on the More button.
  const [moreOpen, setMoreOpen] = useState(false)
  const focusMoreRef = useRef(false)
  const selectExternal = useCallback((id: string | null): void => {
    setExtActive(id)
    setExtButtons(0)
  }, [])
  const docked = props.external !== undefined && state.screen === 'station' && state.stationId !== null ? state.stationId : null
  const extTabs = docked !== null && props.external !== undefined ? props.external.list(docked) : NO_TABS
  const signals = docked !== null ? (props.external?.signals?.(docked) ?? null) : null
  const extView: ExternalView = { tabs: extTabs, active: extActive, buttons: extButtons, signals, moreOpen }
  const extRef = useRef(extView)
  extRef.current = extView
  useEffect(() => {
    setExtActive(null)
    setMoreOpen(false)
  }, [state.screen, state.stationId])
  const closeMore = useCallback((): void => {
    focusMoreRef.current = true
    setMoreOpen(false)
  }, [])
  // Esc (and P or pad Start, through the loop's Esc stack) closes the drawer first.
  const moreOpenRef = useRef(moreOpen)
  moreOpenRef.current = moreOpen
  useEffect(() => {
    const stack = props.escape
    if (stack === undefined || !moreOpen) return undefined
    const handler = (): boolean => {
      if (!moreOpenRef.current) return false
      closeMore()
      return true
    }
    stack.push(handler)
    return () => {
      const i = stack.lastIndexOf(handler)
      if (i >= 0) stack.splice(i, 1)
    }
  }, [props.escape, moreOpen, closeMore])

  // The view: screen models and focus groups. The focus memory picks the outfitting slot, so it is read first.
  const view = buildView(data, state, titleView, focusRef.current.memory, extView)
  if (contextRef.current !== view.contextKey) {
    contextRef.current = view.contextKey
    focusRef.current = initialFocus(view.groups, view.preferred, view.seed, view.start)
    // CL-6: the drawer closed without a pick: the focus goes back to the More button.
    const more = view.screen.kind === 'station' ? moreSlot(view.screen.tabGroups) : -1
    if (focusMoreRef.current && more >= 0) focusRef.current = focusAt(view.groups, focusRef.current, G.tabs, more)
    scrollRef.current = true
  }
  focusMoreRef.current = false
  // CL-6: the session's "new" dots (a tab promoted to the bar keeps its dot until opened).
  const dotsRef = useRef<TabDots>(NO_DOTS)
  if (view.screen.kind === 'station') dotsRef.current = trackPromotions(dotsRef.current, view.screen.tabGroups, activeTabId(view.screen))
  focusRef.current = normalizeFocus(view.groups, focusRef.current)
  const focus = focusRef.current
  const groupsRef = useRef(view.groups)
  groupsRef.current = view.groups
  if (state.screen !== 'title' && titleView !== 'main') setTitleView('main')

  const setFocus = useCallback((next: FocusState, scroll: boolean): void => {
    if (next === focusRef.current) return
    focusRef.current = next
    if (scroll) scrollRef.current = true
    rerender()
  }, [])

  const actions = state.actions
  const outcome = useCallback((o: CmdOutcome): void => {
    setLocalError(o.ok ? null : o.error)
  }, [])

  const ctl: UiController = useMemo(
    () => ({
      data,
      actions,
      cfg,
      run(action: () => CmdOutcome): void {
        try {
          outcome(action())
        } catch (err) {
          logger.error('Station UI action failed', err)
          setLocalError(errorOf(err))
        }
      },
      runAsync(action: () => Promise<CmdOutcome>): void {
        action().then(outcome, (err: unknown) => {
          logger.error('Station UI action failed', err)
          setLocalError(errorOf(err))
        })
      },
      exportSlot(slot: SaveSlotId): void {
        actions.exportSave(slot).then(
          (exp) => {
            if (exp === null) setLocalError({ code: 'emptySlot', message: 'That slot is empty.' })
            else downloadText(exp.fileName, exp.text)
          },
          (err: unknown) => {
            logger.error('Save export failed', err)
            setLocalError(errorOf(err))
          },
        )
      },
      importFile(slot?: ManualSlotId): void {
        importSlotRef.current = slot
        fileRef.current?.click()
      },
      launch(): void {
        // No pointer lock while Launch is off (models still loading, or a save loading: the load hold).
        if (!launchModel(store.getState()).enabled) return
        // Synchronous inside the gesture (click, L key); the loop keeps the lock once the hold is released.
        try {
          lockRef.current()
        } catch (err) {
          logger.warn('Pointer lock request failed', err)
        }
        try {
          outcome(actions.launch())
        } catch (err) {
          logger.error('Launch failed', err)
          setLocalError(errorOf(err))
        }
      },
    }),
    [data, actions, cfg, outcome, store],
  )

  /** CL-6: shows tab `id` (M2's by name, an external one as `ext-<id>`) and closes the drawer. */
  const openTab = (sv: StationView, id: string): void => {
    setMoreOpen(false)
    const ext = sv.external.find((t) => `ext-${t.id}` === id)
    if (ext !== undefined) {
      if (ext.id !== sv.externalTab) selectExternal(ext.id)
      return
    }
    const tab = sv.tabs.find((t) => t === id)
    if (tab === undefined) return
    if (sv.externalTab !== null) selectExternal(null)
    if (tab !== sv.tab) ctl.run(() => store.getState().actions.setTab(tab))
  }

  // Focus API for the focusable items.
  const api: FocusApi = useMemo(
    () => ({
      focused: focusKey(focus.group, focus.index),
      register(key: string, handlers: FocusHandlers): () => void {
        registry.current.set(key, handlers)
        return () => {
          if (registry.current.get(key) === handlers) registry.current.delete(key)
        }
      },
      hover(group: string, index: number): void {
        setFocus(focusAt(groupsRef.current, focusRef.current, group, index), false)
      },
    }),
    [focus.group, focus.index, setFocus],
  )

  // Keyboard and gamepad input, synchronously from the feature's listeners.
  const handleRef = useRef<(input: NavInput) => void>(() => undefined)
  handleRef.current = (input: NavInput): void => {
    const current = store.getState()
    const v = buildView(data, current, titleView, focusRef.current.memory, extRef.current)
    // The load hold: while a save loads, keys and the pad do nothing (not even focus moves).
    if (v.inputLocked) return
    const step = navigate(v.groups, focusRef.current, input)
    setFocus(step.focus, true)
    const key = focusKey(step.focus.group, step.focus.index)
    const r = step.result
    switch (r.kind) {
      case 'activate':
        registry.current.get(key)?.activate?.()
        return
      case 'adjust':
        registry.current.get(key)?.adjust?.(r.delta)
        return
      case 'back':
        if (v.confirm !== null) {
          const id = v.confirm.id
          ctl.runAsync(() => current.actions.answerConfirm(id, false))
        } else if (v.screen.kind === 'title' && titleView !== 'main') setTitleView('main')
        else if (v.screen.kind === 'death' || v.screen.kind === 'ownership') ctl.run(() => current.actions.dismissScreen())
        else if (v.screen.kind === 'station' && extRef.current.moreOpen === true) closeMore()
        else if (v.screen.kind === 'station') setFocus(focusAt(v.groups, focusRef.current, G.tabs, activeTabIndex(v.screen)), true)
        return
      case 'prevTab':
      case 'nextTab':
        if (v.confirm === null && v.screen.kind === 'station') {
          // CL-6: Q/E walk the bar's primary tabs and the More button (focused there, it opens with Enter/A).
          const sv = v.screen
          const more = moreSlot(sv.tabGroups)
          const count = sv.tabGroups.primary.length + (more >= 0 ? 1 : 0)
          if (count === 0) return
          const onMore = more >= 0 && focusRef.current.group === G.tabs && focusRef.current.index === more
          const at = onMore ? more : activeTabIndex(sv)
          const next = (Math.max(0, at) + (r.kind === 'nextTab' ? 1 : -1) + count) % count
          if (next === more) {
            if (extRef.current.moreOpen === true) setMoreOpen(false)
            setFocus(focusAt(v.groups, focusRef.current, G.tabs, more), true)
          } else openTab(sv, sv.tabGroups.primary[next].id)
        }
        return
      case 'launch':
        if (v.confirm === null && v.screen.kind === 'station' && v.screen.launch.enabled) ctl.launch()
        return
      case 'moved':
      case 'none':
        return
    }
  }
  useEffect(() => nav.subscribe((input) => handleRef.current(input)), [nav])

  // Keep the focused item in view after keyboard and pad moves.
  useLayoutEffect(() => {
    if (!scrollRef.current) return
    scrollRef.current = false
    const el = rootRef.current?.querySelector(`[data-nav="${focusKey(focus.group, focus.index)}"]`)
    if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  })

  // A refusal from the store (or a local one) shows until the next action.
  useEffect(() => {
    setLocalError(null)
  }, [view.contextKey])

  const onFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const input = e.currentTarget
    const file = input.files?.[0]
    input.value = ''
    if (file === undefined) return
    const slot = importSlotRef.current
    file.text().then(
      (text) => ctl.runAsync(() => actions.importSave(text, slot)),
      (err: unknown) => setLocalError(errorOf(err)),
    )
  }

  const s = view.screen
  if (s.kind === 'none') return null
  const error = state.lastError ?? localError
  const focusedOffer = focus.memory[G.offers] ?? 0

  return (
    <FocusContext.Provider value={api}>
      <div
        ref={rootRef}
        className="ui-app"
        data-screen={s.kind}
        data-loading={view.inputLocked ? '1' : undefined}
        aria-busy={view.inputLocked}
        // The load hold: clicks and DOM focus are off (M2's buttons and the external tabs alike) until the save docks.
        inert={view.inputLocked}
        onMouseDown={(e) => {
          // Keep DOM focus off buttons: keys go through the focus model only (no double activation).
          if (!(e.target instanceof HTMLInputElement)) e.preventDefault()
        }}
      >
        {s.kind === 'title' ? <TitleScreen ctl={ctl} model={s.title} view={s.view} setView={setTitleView} /> : null}
        {s.kind === 'station' ? (
          <StationScreen
            ctl={ctl}
            view={s}
            notices={state.notices}
            error={error}
            focusedOffer={focusedOffer}
            loading={view.inputLocked}
            external={props.external === undefined ? undefined : { tabs: extTabs, select: selectExternal, buttons: setExtButtons }}
            more={{
              open: moreOpen,
              toggle: () => (moreOpen ? closeMore() : setMoreOpen(true)),
              openTab: (id) => openTab(s, id),
              dots: dotsRef.current.fresh,
              unread: signals?.wireUnread ?? 0,
            }}
          />
        ) : null}
        {s.kind === 'death' ? <DeathScreen ctl={ctl} model={s.death} /> : null}
        {s.kind === 'ownership' ? <OwnershipScreen ctl={ctl} model={s.ownership} /> : null}
        {view.inputLocked && s.kind !== 'station' ? (
          <p className="ui-error ui-error--float" role="status" data-testid="ui-loading">
            {LOADING_LABEL}
          </p>
        ) : null}
        {s.kind === 'title' && error !== null && !view.inputLocked ? (
          <p className="ui-error ui-error--float" role="alert">
            {'⚠'} {error.message}
          </p>
        ) : null}
        {view.confirm !== null ? (
          <div className="ui-scrim" role="dialog" aria-modal="true" aria-labelledby="ui-confirm-title">
            <div className="ui-panel ui-confirm">
              <h2 id="ui-confirm-title" className="ui-panel__title">
                {view.confirm.title}
              </h2>
              <p>{view.confirm.message}</p>
              <div className="ui-confirm__buttons">
                <FocusButton group={G.confirm} index={0} testId="confirm-accept" onActivate={() => ctl.runAsync(() => actions.answerConfirm(view.confirm?.id ?? '', true))}>
                  {view.confirm.confirmLabel}
                </FocusButton>
                <FocusButton group={G.confirm} index={1} testId="confirm-cancel" className="ui-btn--quiet" onActivate={() => ctl.runAsync(() => actions.answerConfirm(view.confirm?.id ?? '', false))}>
                  {view.confirm.cancelLabel}
                </FocusButton>
              </div>
            </div>
          </div>
        ) : null}
        <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={onFile} data-testid="saves-import-file" />
      </div>
    </FocusContext.Provider>
  )
}
